import { useEffect, useState } from 'react';

import type Enums from '../types/api/Enums';
import type { ImportedDiscount } from '../types/api/ImportedDiscount/ImportedDiscount';
import type { QuoteProductDiscount } from '../types/api/QuoteProductDiscount/QuoteProductDiscount';
import {
  appliedWithinNetPriceFloor,
  dropsNetPriceBelowZero,
  grossPriceOf,
  hasPendingDiscounts,
  resolveDiscountsAtGross,
  settledNetPrice,
} from '../utils/discounts';
import { hasValue } from '../utils/equality';
import { isValidUUID } from '../utils/uuid';

type Args = {
  companyID: string | null;
  productID: string;
  deliveryType: Enums.ProductDeliveryType | null;
  netUnitPrice: number;
  applied: readonly QuoteProductDiscount[];
  matching: readonly ImportedDiscount[];
  isSuccess: boolean;
  isError: boolean;
  readOnly?: boolean;
  isPriceSettled?: boolean;
  onChange: (applied: QuoteProductDiscount[], netUnitPrice: number) => void;
};

export const useReconcileDiscounts = ({
  companyID,
  productID,
  deliveryType,
  netUnitPrice,
  applied,
  matching,
  isSuccess,
  isError,
  readOnly = false,
  isPriceSettled = true,
  onChange,
}: Args): {
  unappliedNames: string[];
  unofferedNames: string[];
  unverifiedNames: string[];
  clearUnappliedNames: () => void;
} => {
  const [unappliedNames, setUnappliedNames] = useState<string[]>([]);
  const [unofferedNames, setUnofferedNames] = useState<string[]>([]);
  const grossPrice = grossPriceOf(applied, netUnitPrice);
  const unverifiedNames =
    isError && !readOnly && isPriceSettled && hasValue(companyID)
      ? applied.filter((d) => !isValidUUID(d.id)).map((d) => d.name)
      : [];

  useEffect(() => {
    setUnappliedNames([]);
    setUnofferedNames([]);
  }, [companyID, productID, deliveryType]);

  useEffect(() => {
    if (readOnly || applied.length === 0 || !isPriceSettled) {
      return;
    }

    if (!hasValue(companyID)) {
      onChange([], grossPrice);
      return;
    }

    if (!isSuccess) {
      if (isError && dropsNetPriceBelowZero(applied, grossPrice)) {
        const kept = appliedWithinNetPriceFloor(applied, grossPrice);
        setUnappliedNames(applied.filter((d) => !kept.includes(d)).map((d) => d.name));
        onChange(kept, settledNetPrice(kept, grossPrice));
      }
      return;
    }

    if (!hasPendingDiscounts(applied)) {
      return;
    }

    const resolved = resolveDiscountsAtGross(applied, matching, grossPrice);
    if (
      resolved.discounts.length !== applied.length ||
      resolved.discounts.some((d, i) => d !== applied[i])
    ) {
      const retainedIds = new Set(
        [...resolved.discounts, ...resolved.belowZero].map((d) => d.importedDiscountId),
      );
      setUnappliedNames(resolved.belowZero.map((d) => d.name));
      setUnofferedNames(
        applied.filter((d) => !retainedIds.has(d.importedDiscountId)).map((d) => d.name),
      );
      onChange(resolved.discounts, settledNetPrice(resolved.discounts, grossPrice));
    }
  }, [
    companyID,
    isSuccess,
    isError,
    readOnly,
    isPriceSettled,
    applied,
    matching,
    grossPrice,
    onChange,
  ]);

  return {
    unappliedNames,
    unofferedNames,
    unverifiedNames,
    clearUnappliedNames: (): void => {
      setUnappliedNames([]);
      setUnofferedNames([]);
    },
  };
};
